import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Card,
    Col,
    DatePicker,
    Empty,
    Grid,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import type {ColumnsType} from "antd/es/table";
import {
    AuditOutlined,
    CalendarOutlined,
    DollarOutlined,
    UserOutlined,
    WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../../api";

const {Title, Text} = Typography;
const {useBreakpoint} = Grid;

type Agent = {
    id: number;
    name: string;
    program: string;
};

type TripRow = {
    id: number;
    mmid: string | number | null;
    checkin_date: string | null;
    checkout_date: string | null;
    buyin_amount: string | number | null;
    rebuys: string | number | null;
    final_balance: string | number | null;
    win_loss: string | number | null;
    bonus_amount: string | number | null;
    expenses: string | number | null;
};

type MonthlySettlement = {
    month: number;
    monthly_win_loss: string | number;
    opening_carryover: string | number;
    adjusted_profit: string | number;
    commissionable_profit: string | number;
    closing_carryover: string | number;
    agent_fee_rate: string | number;
    agent_fees: string | number;
    trips: TripRow[];
};

type SettlementResponse = {
    agent: Agent;
    year: number;
    months: MonthlySettlement[];
};

const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const shortMonthNames = [
    "",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

const toNumber = (value: unknown): number => {
    if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "Pending"
    ) {
        return 0;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatKRW = (value: unknown): string => {
    return `₩${Math.round(toNumber(value)).toLocaleString("en-US")}`;
};

const formatPercent = (value: unknown): string => {
    return `${(toNumber(value) * 100).toFixed(2)}%`;
};

const noWrapStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
    overflowWrap: "normal",
};

const numberStyle: React.CSSProperties = {
    ...noWrapStyle,
    fontVariantNumeric: "tabular-nums",
};

const renderHeader = (label: string) => (
    <span style={noWrapStyle}>{label}</span>
);

const renderMoney = (value: unknown) => {
    const num = toNumber(value);

    return (
        <span
            style={{
                ...numberStyle,
                color: num < 0 ? "#cf1322" : undefined,
                fontWeight: num !== 0 ? 600 : 400,
            }}
        >
            {formatKRW(num)}
        </span>
    );
};

const renderPlainMoney = (value: unknown) => (
    <span style={numberStyle}>{formatKRW(value)}</span>
);

const renderPercent = (value: unknown) => (
    <span style={numberStyle}>{formatPercent(value)}</span>
);

const InfoTile = ({
                      icon,
                      label,
                      value,
                      tone = "default",
                  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    tone?: "default" | "blue" | "green" | "red";
}) => {
    const toneMap = {
        default: {
            bg: "#f3f4f6",
            color: "#374151",
            border: "#e5e7eb",
        },
        blue: {
            bg: "#e6f4ff",
            color: "#1677ff",
            border: "#91caff",
        },
        green: {
            bg: "#f6ffed",
            color: "#52c41a",
            border: "#b7eb8f",
        },
        red: {
            bg: "#fff1f0",
            color: "#cf1322",
            border: "#ffa39e",
        },
    }[tone];

    return (
        <Card
            size="small"
            style={{
                height: "100%",
                borderRadius: 14,
                border: `1px solid ${toneMap.border}`,
                background: "#fff",
            }}
            bodyStyle={{padding: 14}}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    minWidth: 0,
                }}
            >
                <div
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 12,
                        background: toneMap.bg,
                        color: toneMap.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </div>

                <div style={{minWidth: 0, flex: 1}}>
                    <Text type="secondary" style={{fontSize: 12}}>
                        {label}
                    </Text>

                    <div
                        style={{
                            fontSize: 17,
                            fontWeight: 800,
                            lineHeight: 1.25,
                            marginTop: 2,
                            wordBreak: "keep-all",
                            overflowWrap: "normal",
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {value}
                    </div>
                </div>
            </div>
        </Card>
    );
};

const MobileField = ({
                         label,
                         value,
                     }: {
    label: string;
    value: React.ReactNode;
}) => (
    <div>
        <Text type="secondary" style={{fontSize: 12}}>
            {label}
        </Text>
        <div
            style={{
                ...numberStyle,
                fontWeight: 700,
                marginTop: 2,
            }}
        >
            {value}
        </div>
    </div>
);

const AgentPortalSettlementsPage: React.FC = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const tableScrollX = screens.xl ? undefined : "max-content";

    const [loading, setLoading] = useState(false);
    const [agent, setAgent] = useState<Agent | null>(null);
    const [year, setYear] = useState<number>(dayjs().year());
    const [months, setMonths] = useState<MonthlySettlement[]>([]);
    const [error, setError] = useState<string | null>(null);

    const fetchSettlements = async (selectedYear = year) => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get<SettlementResponse>(
                "/daia/agent-portal/settlements/",
                {
                    params: {
                        year: selectedYear,
                    },
                }
            );

            setAgent(response.data.agent);
            setYear(response.data.year);
            setMonths(response.data.months || []);
        } catch (err: any) {
            console.error(err);

            const msg =
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Failed to load settlement data.";

            setError(msg);
            message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettlements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const summary = useMemo(() => {
        const yearWinLoss = months.reduce(
            (sum, row) => sum + toNumber(row.monthly_win_loss),
            0
        );

        const agentFees = months.reduce(
            (sum, row) => sum + toNumber(row.agent_fees),
            0
        );

        const tripCount = months.reduce(
            (sum, row) => sum + (row.trips?.length || 0),
            0
        );

        const activeMonths = months.filter((row) => {
            return (
                toNumber(row.monthly_win_loss) !== 0 ||
                toNumber(row.agent_fees) !== 0 ||
                toNumber(row.commissionable_profit) !== 0 ||
                (row.trips?.length || 0) > 0
            );
        });

        const latestMonthWithData = [...activeMonths].sort(
            (a, b) => b.month - a.month
        )[0];

        const endingCarryover = latestMonthWithData
            ? toNumber(latestMonthWithData.closing_carryover)
            : months.length
                ? toNumber(months[months.length - 1].closing_carryover)
                : 0;

        return {
            yearWinLoss,
            agentFees,
            tripCount,
            endingCarryover,
        };
    }, [months]);

    const handleYearChange = (date: dayjs.Dayjs | null) => {
        if (!date) return;

        const selectedYear = date.year();
        setYear(selectedYear);
        fetchSettlements(selectedYear);
    };

    const tripColumns: ColumnsType<TripRow> = [
        {
            title: renderHeader("MMID"),
            dataIndex: "mmid",
            key: "mmid",
            fixed: "left",
            width: 110,
            render: (value) => <span style={numberStyle}>{value || "-"}</span>,
        },
        {
            title: renderHeader("Check-in"),
            dataIndex: "checkin_date",
            key: "checkin_date",
            width: 120,
            render: (value) => <span style={numberStyle}>{value || "-"}</span>,
        },
        {
            title: renderHeader("Check-out"),
            dataIndex: "checkout_date",
            key: "checkout_date",
            width: 120,
            render: (value) => <span style={numberStyle}>{value || "-"}</span>,
        },
        {
            title: renderHeader("Buy-in"),
            dataIndex: "buyin_amount",
            key: "buyin_amount",
            align: "right",
            width: 150,
            render: renderPlainMoney,
        },
        {
            title: renderHeader("Rebuys"),
            dataIndex: "rebuys",
            key: "rebuys",
            align: "right",
            width: 150,
            render: renderPlainMoney,
        },
        {
            title: renderHeader("Final Balance"),
            dataIndex: "final_balance",
            key: "final_balance",
            align: "right",
            width: 160,
            render: (value) =>
                value === null ? <Tag>Pending</Tag> : renderPlainMoney(value),
        },
        {
            title: renderHeader("Win/Loss"),
            dataIndex: "win_loss",
            key: "win_loss",
            align: "right",
            width: 150,
            render: renderMoney,
        },
        {
            title: renderHeader("Bonus"),
            dataIndex: "bonus_amount",
            key: "bonus_amount",
            align: "right",
            width: 140,
            render: renderPlainMoney,
        },
        {
            title: renderHeader("Expenses"),
            dataIndex: "expenses",
            key: "expenses",
            align: "right",
            width: 140,
            render: renderPlainMoney,
        },
    ];

    const columns: ColumnsType<MonthlySettlement> = [
        {
            title: renderHeader("Month"),
            dataIndex: "month",
            key: "month",
            fixed: "left",
            width: 120,
            render: (month: number) => (
                <span style={{...noWrapStyle, fontWeight: 700}}>
                    {monthNames[month]}
                </span>
            ),
        },
        {
            title: renderHeader("Win/Loss"),
            dataIndex: "monthly_win_loss",
            key: "monthly_win_loss",
            align: "right",
            width: 150,
            render: renderMoney,
        },
        {
            title: renderHeader("Opening Carryover"),
            dataIndex: "opening_carryover",
            key: "opening_carryover",
            align: "right",
            width: 180,
            render: renderMoney,
        },
        {
            title: renderHeader("Adjusted Monthly Profit"),
            dataIndex: "adjusted_profit",
            key: "adjusted_profit",
            align: "right",
            width: 210,
            render: renderMoney,
        },
        {
            title: renderHeader("Commissionable Profit"),
            dataIndex: "commissionable_profit",
            key: "commissionable_profit",
            align: "right",
            width: 210,
            render: renderPlainMoney,
        },
        {
            title: renderHeader("Rate"),
            dataIndex: "agent_fee_rate",
            key: "agent_fee_rate",
            align: "right",
            width: 100,
            render: renderPercent,
        },
        {
            title: renderHeader("Commissions"),
            dataIndex: "agent_fees",
            key: "agent_fees",
            align: "right",
            width: 150,
            render: renderPlainMoney,
        },
        {
            title: renderHeader("Closing Carryover"),
            dataIndex: "closing_carryover",
            key: "closing_carryover",
            align: "right",
            width: 180,
            render: renderMoney,
        },
    ];

    const renderTripCard = (trip: TripRow) => (
        <Card
            key={trip.id}
            size="small"
            style={{
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#fafafa",
            }}
            bodyStyle={{padding: 14}}
        >
            <Space direction="vertical" size={10} style={{width: "100%"}}>
                <Row justify="space-between" align="middle" gutter={[8, 8]}>
                    <Col>
                        <Text type="secondary">MMID</Text>
                        <div
                            style={{
                                ...numberStyle,
                                fontSize: 17,
                                fontWeight: 800,
                            }}
                        >
                            {trip.mmid || "-"}
                        </div>
                    </Col>
                    <Col>
                        {toNumber(trip.win_loss) >= 0 ? (
                            <Tag color="blue">Loss</Tag>
                        ) : (
                            <Tag color="red">Win</Tag>
                        )}
                    </Col>
                </Row>

                <Row gutter={[12, 12]}>
                    <Col span={12}>
                        <MobileField label="Check-in" value={trip.checkin_date || "-"}/>
                    </Col>
                    <Col span={12}>
                        <MobileField label="Check-out" value={trip.checkout_date || "-"}/>
                    </Col>
                    <Col span={12}>
                        <MobileField label="Buy-in" value={formatKRW(trip.buyin_amount)}/>
                    </Col>
                    <Col span={12}>
                        <MobileField label="Rebuys" value={formatKRW(trip.rebuys)}/>
                    </Col>
                    <Col span={12}>
                        <MobileField
                            label="Final Balance"
                            value={
                                trip.final_balance === null
                                    ? "Pending"
                                    : formatKRW(trip.final_balance)
                            }
                        />
                    </Col>
                    <Col span={12}>
                        <MobileField label="Win/Loss" value={renderMoney(trip.win_loss)}/>
                    </Col>
                    <Col span={12}>
                        <MobileField label="Bonus" value={formatKRW(trip.bonus_amount)}/>
                    </Col>
                    <Col span={12}>
                        <MobileField label="Expenses" value={formatKRW(trip.expenses)}/>
                    </Col>
                </Row>
            </Space>
        </Card>
    );

    const renderMobileMonthCard = (record: MonthlySettlement) => {
        const tripCount = record.trips?.length || 0;

        return (
            <Card
                key={record.month}
                style={{
                    borderRadius: 16,
                    border: "1px solid #e5e7eb",
                }}
                bodyStyle={{padding: 16}}
            >
                <Space direction="vertical" size={14} style={{width: "100%"}}>
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col>
                            <Text type="secondary">Month</Text>
                            <div style={{fontSize: 21, fontWeight: 800}}>
                                {monthNames[record.month]}
                            </div>
                        </Col>
                        <Col>
                            <Tag color={tripCount ? "blue" : "default"}>
                                {tripCount} Trip{tripCount === 1 ? "" : "s"}
                            </Tag>
                        </Col>
                    </Row>

                    <Row gutter={[12, 12]}>
                        <Col span={12}>
                            <MobileField
                                label="Win/Loss"
                                value={renderMoney(record.monthly_win_loss)}
                            />
                        </Col>
                        <Col span={12}>
                            <MobileField
                                label="Commissions"
                                value={formatKRW(record.agent_fees)}
                            />
                        </Col>
                        <Col span={12}>
                            <MobileField
                                label="Opening Carryover"
                                value={renderMoney(record.opening_carryover)}
                            />
                        </Col>
                        <Col span={12}>
                            <MobileField
                                label="Closing Carryover"
                                value={renderMoney(record.closing_carryover)}
                            />
                        </Col>
                        <Col span={12}>
                            <MobileField
                                label="Adjusted Monthly Profit"
                                value={renderMoney(record.adjusted_profit)}
                            />
                        </Col>
                        <Col span={12}>
                            <MobileField
                                label="Commissionable Profit"
                                value={formatKRW(record.commissionable_profit)}
                            />
                        </Col>
                        <Col span={12}>
                            <MobileField
                                label="Rate"
                                value={formatPercent(record.agent_fee_rate)}
                            />
                        </Col>
                    </Row>

                    {tripCount > 0 && (
                        <div>
                            <Text strong>Trip Details</Text>
                            <div style={{display: "grid", gap: 12, marginTop: 10}}>
                                {record.trips.map(renderTripCard)}
                            </div>
                        </div>
                    )}
                </Space>
            </Card>
        );
    };

    return (
        <div>
            <Space direction="vertical" size="large" style={{width: "100%"}}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} sm={16}>
                        <Title level={3} style={{marginBottom: 4}}>
                            Settlements
                        </Title>
                        <Text type="secondary">
                            Monthly settlement statement and trip activity.
                        </Text>
                    </Col>

                    <Col xs={24} sm={8} style={{textAlign: screens.sm ? "right" : "left"}}>
                        <DatePicker
                            picker="year"
                            value={dayjs(`${year}-01-01`)}
                            onChange={handleYearChange}
                            allowClear={false}
                            style={{width: screens.sm ? 140 : "100%"}}
                        />
                    </Col>
                </Row>

                {error && (
                    <Alert
                        type="error"
                        message="Unable to load settlements"
                        description={error}
                        showIcon
                    />
                )}

                {agent && (
                    <Card
                        style={{
                            borderRadius: 18,
                            border: "1px solid #e5e7eb",
                            background:
                                "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                        }}
                        bodyStyle={{padding: 18}}
                    >
                        <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} lg={8} xxl={8}>
                                <InfoTile
                                    icon={<UserOutlined/>}
                                    label="Agent"
                                    value={agent.name}
                                    tone="default"
                                />
                            </Col>

                            <Col xs={12} sm={12} lg={4} xxl={4}>
                                <InfoTile
                                    icon={<AuditOutlined/>}
                                    label="Program"
                                    value={<Tag color="blue">{agent.program}</Tag>}
                                    tone="blue"
                                />
                            </Col>

                            <Col xs={12} sm={12} lg={4} xxl={4}>
                                <InfoTile
                                    icon={<CalendarOutlined/>}
                                    label="Year"
                                    value={year}
                                    tone="default"
                                />
                            </Col>

                            <Col xs={24} sm={12} lg={6} xxl={4}>
                                <InfoTile
                                    icon={<DollarOutlined/>}
                                    label="Commissions"
                                    value={formatKRW(summary.agentFees)}
                                    tone="green"
                                />
                            </Col>

                            <Col xs={24} sm={12} lg={6} xxl={4}>
                                <InfoTile
                                    icon={<WalletOutlined/>}
                                    label="Carryover"
                                    value={formatKRW(summary.endingCarryover)}
                                    tone={summary.endingCarryover < 0 ? "red" : "default"}
                                />
                            </Col>
                        </Row>
                    </Card>
                )}

                <Spin spinning={loading}>
                    <Card
                        title="Monthly Statement"
                        style={{
                            borderRadius: 18,
                            border: "1px solid #e5e7eb",
                        }}
                        bodyStyle={{padding: isMobile ? 12 : 16}}
                    >
                        {months.length ? (
                            isMobile ? (
                                <Space direction="vertical" size={14} style={{width: "100%"}}>
                                    {months.map(renderMobileMonthCard)}
                                </Space>
                            ) : (
                                <Table
                                    className="agent-settlements-table"
                                    rowKey="month"
                                    columns={columns}
                                    dataSource={months}
                                    bordered
                                    pagination={false}
                                    size="middle"
                                    scroll={tableScrollX ? {x: tableScrollX} : undefined}
                                    expandable={{
                                        expandedRowRender: (record) =>
                                            record.trips?.length ? (
                                                <Table
                                                    className="agent-settlements-table"
                                                    rowKey="id"
                                                    columns={tripColumns}
                                                    dataSource={record.trips}
                                                    pagination={false}
                                                    size="small"
                                                    bordered
                                                    scroll={{x: "max-content"}}
                                                />
                                            ) : (
                                                <Empty description="No trips for this month"/>
                                            ),
                                        rowExpandable: (record) =>
                                            record.trips && record.trips.length > 0,
                                    }}
                                />
                            )
                        ) : (
                            <Empty description="No settlement data for this year"/>
                        )}
                    </Card>
                </Spin>
            </Space>
        </div>
    );
};

export default AgentPortalSettlementsPage;